import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import config from '../config.js';

/** Songs per page of the crate grid. */
export const CRATE_PAGE_SIZE = 30;

/** The most songs whose flags one refresh reads again (the API's page limit). */
export const CRATE_REFRESH_MAX = 100;

/** How long typing must pause before the search is sent, in ms. */
export const CRATE_SEARCH_DEBOUNCE_MS = 300;

/** The crate filters that take several values, and their query parameters. */
const MULTI = [['decades', 'decade'], ['genreIds', 'genre_id'], ['moods', 'mood'], ['tagIds', 'tag_id']];

/**
 * The query string of one crate page: the filters that are set, and the page.
 * Each of decades, genreIds, moods and tagIds is a list, any value of which
 * matches; different filters must all match (ENG-708). `sort` is the crate's
 * sort chip (most_requested or new); title order otherwise.
 */
export const crateQuery = (filters, page) => {
  const params = new URLSearchParams();
  const term = (filters.search ?? '').trim();
  if (term) params.set('search', term);
  MULTI.forEach(([key, param]) => {
    (filters[key] ?? []).forEach((value) => params.append(param, String(value)));
  });
  if (filters.sort) params.set('sort_by', filters.sort);
  params.set('page', String(page));
  params.set('per_page', String(CRATE_PAGE_SIZE));
  return params.toString();
};

const normalizeSong = (song) => ({
  id: song.id,
  title: song.title ?? '',
  artist: song.artist ?? '',
  artworkUrl: song.artwork_url || '',
  playedTonight: Boolean(song.played_tonight),
  repeatBlocked: Boolean(song.repeat_blocked),
});

const EMPTY = { songs: [], total: 0, crateTotal: 0, page: 0, loading: false, error: false };

/**
 * The band's song crate (`GET /public/bands/{id}/songs`, ENG-698) for the
 * stream page's crate panel (mockup 16, ENG-697), a page at a time, with
 * `total` matching the filters and `crateTotal` the whole crate (ENG-708).
 *
 * Changing a filter starts over from page one; a changed search waits until
 * typing pauses. Only the latest read may set state, so an answer to an
 * earlier filter that arrives late can't replace the current one.
 * `loadMore()` appends the next page while there is one.
 */
export const useCrate = (bandId, filters, { enabled = true } = {}) => {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const [state, setState] = useState(EMPTY);
  const seqRef = useRef(0);
  const [search, setSearch] = useState(filters.search ?? '');

  useEffect(() => {
    const next = filters.search ?? '';
    if (next.trim() === search.trim()) return undefined;
    const timer = setTimeout(() => setSearch(next), CRATE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters.search, search]);

  // The filters as a string, so a new array with the same values is no
  // change.
  const filterKey = crateQuery({ ...filters, search }, 1);

  const read = useCallback((page, query = filterKey) => {
    if (!bandId || !enabled) return;
    const seq = ++seqRef.current;
    setState((prev) => ({ ...prev, loading: true, error: false }));
    const params = new URLSearchParams(query);
    params.set('page', String(page));
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/public/bands/${bandId}/songs?${params}`)
      .then((answer) => {
        if (seq !== seqRef.current) return;
        const songs = Array.isArray(answer?.songs) ? answer.songs.map(normalizeSong) : [];
        const total = Number(answer?.total) || 0;
        setState((prev) => ({
          songs: page === 1 ? songs : [...prev.songs, ...songs],
          total,
          crateTotal: Number(answer?.crate_total) || total,
          page,
          loading: false,
          error: false,
        }));
      })
      .catch(() => {
        if (seq !== seqRef.current) return;
        setState((prev) => ({ ...prev, loading: false, error: true }));
      });
  }, [bandId, enabled, filterKey]);

  useEffect(() => {
    setState(EMPTY);
    read(1);
  }, [read]);

  const hasMore = state.songs.length < state.total;
  const loadMore = useCallback(() => {
    if (state.loading || !hasMore) return;
    read(state.page + 1);
  }, [state.loading, state.page, hasMore, read]);

  const retry = useCallback(() => {
    if (state.loading) return;
    read(state.songs.length ? state.page + 1 : 1);
  }, [read, state.loading, state.page, state.songs.length]);

  /*
   * The played-tonight flags of the songs already shown, read again after a
   * song is played: one read of the first CRATE_REFRESH_MAX songs, whose
   * flags replace the shown ones. The list itself is left as it is.
   */
  const songsRef = useRef(state.songs);
  songsRef.current = state.songs;
  const refreshFlags = useCallback(() => {
    const shown = songsRef.current.length;
    if (!bandId || !enabled || shown === 0) return;
    const seq = seqRef.current;
    const query = new URLSearchParams(filterKey);
    query.set('per_page', String(Math.min(shown, CRATE_REFRESH_MAX)));
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/public/bands/${bandId}/songs?${query}`)
      .then((answer) => {
        if (seq !== seqRef.current || !Array.isArray(answer?.songs)) return;
        const flags = new Map(answer.songs.map((s) => [s.id, normalizeSong(s)]));
        setState((prev) => ({
          ...prev,
          songs: prev.songs.map((s) => {
            const fresh = flags.get(s.id);
            return fresh ? { ...s, playedTonight: fresh.playedTonight, repeatBlocked: fresh.repeatBlocked } : s;
          }),
        }));
      })
      .catch(() => {
        // The flags are a hint; the queue refuses a blocked repeat anyway.
      });
  }, [bandId, enabled, filterKey]);

  return { ...state, hasMore, loadMore, retry, refreshFlags };
};
